import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { StructuredFilterGroupRequest } from "@/components/StructuredSearch";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import {
  buildAnalyticsGraphSearchParams,
  getDateRangeFromFilterGroup,
  mergeAnalyticsGraphFilterGroups,
  REQUEST_ANALYTICS_DATE_RANGE_FIELD,
  TRACE_ANALYTICS_DATE_RANGE_FIELD,
} from "@/components/RequestsAnalytics/utils/mergeAnalyticsGraphFilterGroups";
import { useRequestLogsWorkspaceId } from "@/components/RequestsAnalytics/hooks/useRequestLogsWorkspaceId";
import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import { useUserToken } from "@/context/auth-context";
import type {
  AnalyticsGraphDataSource,
  CustomChartSpecRequest,
  RequestAnalyticsCustomChartsResponse,
} from "@/queries/analytics-custom-charts-types";
import { AnalyticsGraphDataSource as AnalyticsGraphDataSourceValue } from "@/queries/analytics-custom-charts-types";
import { buildRequestLogQueryPayload } from "@/queries/requests/useStructuredRequestsPaginated";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

const ANALYTICS_CHART_QUERY_RETRY_COUNT = 3;

export const useSingleAnalyticsGraphChartData = (
  customChartSpec: CustomChartSpecRequest | undefined,
  options?: {
    dateRangeOverride?: CompleteDateRange;
    dataSource?: AnalyticsGraphDataSource;
    enabled?: boolean;
    filterGroup?: StructuredFilterGroupRequest;
  },
) => {
  const isQueryEnabled = options?.enabled ?? true;
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();
  const isTracesDataSource = options?.dataSource === AnalyticsGraphDataSourceValue.Traces;
  const dateRangeField = isTracesDataSource
    ? TRACE_ANALYTICS_DATE_RANGE_FIELD
    : REQUEST_ANALYTICS_DATE_RANGE_FIELD;
  const analyticsQueryKey = isTracesDataSource ? queryKeys.traces : queryKeys.requests;

  const structuredDateRange = useMemo(
    () =>
      options?.dateRangeOverride ??
      getDateRangeFromFilterGroup(options?.filterGroup, dateRangeField),
    [dateRangeField, options?.dateRangeOverride, options?.filterGroup],
  );

  const effectiveFilterGroup = useMemo(
    () =>
      mergeAnalyticsGraphFilterGroups(options?.filterGroup, undefined, {
        dateRangeField,
        dateRangeOverride: options?.dateRangeOverride,
        preserveDateRangeLocalOffset: true,
      }),
    [dateRangeField, options?.dateRangeOverride, options?.filterGroup],
  );

  const searchParams = useMemo(
    () =>
      buildAnalyticsGraphSearchParams({
        customChartSpec,
        effectiveFilterGroup,
        hasArtifactFilterGroup: true,
      }),
    [customChartSpec, effectiveFilterGroup],
  );
  const endpoint = isTracesDataSource
    ? ENDPOINTS.post_trace_analytics_custom_analytics(workspaceId)
    : ENDPOINTS.post_request_analytics_custom_analytics(workspaceId);
  const requestBody = isTracesDataSource
    ? {
        filterGroup: effectiveFilterGroup,
        customCharts: [customChartSpec!],
      }
    : {
        ...buildRequestLogQueryPayload(searchParams),
        customCharts: [customChartSpec!],
      };

  const query = useQuery<RequestAnalyticsCustomChartsResponse>(
    [
      analyticsQueryKey,
      "analytics-custom-charts",
      "graph-custom-chart",
      userToken,
      workspaceId,
      options?.dataSource,
      searchParams,
    ],
    () =>
      fetch(endpoint, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(requestBody),
      }).then(handleResponse),
    {
      enabled: isQueryEnabled && !!userToken && !!workspaceId && !!customChartSpec,
      keepPreviousData: true,
      retry: ANALYTICS_CHART_QUERY_RETRY_COUNT,
    },
  );

  // Avoid pairing a new date range with a stale chartInterval from keepPreviousData
  // (e.g. 1m interval left over after reset/zoom while refetching for 1d → 1h).
  const chartInterval = query.isPreviousData ? undefined : query.data?.chartInterval;

  return {
    chartInterval,
    chartResponse: query.data?.customCharts?.[0],
    isAnalyticsLoading: query.isFetching || query.isLoading,
    structuredDateRange,
  };
};

export const useBatchedAnalyticsGraphChartData = (
  customChartSpecs: CustomChartSpecRequest[],
  options?: {
    dataSource?: AnalyticsGraphDataSource;
    enabled?: boolean;
    filterGroup?: StructuredFilterGroupRequest;
  },
) => {
  const isQueryEnabled = options?.enabled ?? true;
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();
  const isTracesDataSource = options?.dataSource === AnalyticsGraphDataSourceValue.Traces;
  const dateRangeField = isTracesDataSource
    ? TRACE_ANALYTICS_DATE_RANGE_FIELD
    : REQUEST_ANALYTICS_DATE_RANGE_FIELD;
  const analyticsQueryKey = isTracesDataSource ? queryKeys.traces : queryKeys.requests;

  const structuredDateRange = useMemo(
    () => getDateRangeFromFilterGroup(options?.filterGroup, dateRangeField),
    [dateRangeField, options?.filterGroup],
  );

  const effectiveFilterGroup = useMemo(
    () =>
      mergeAnalyticsGraphFilterGroups(options?.filterGroup, undefined, {
        dateRangeField,
        preserveDateRangeLocalOffset: true,
      }),
    [dateRangeField, options?.filterGroup],
  );

  const searchParams = useMemo(
    () => ({
      filterGroup: effectiveFilterGroup,
      customCharts: customChartSpecs,
    }),
    [customChartSpecs, effectiveFilterGroup],
  );

  const endpoint = isTracesDataSource
    ? ENDPOINTS.post_trace_analytics_custom_analytics(workspaceId)
    : ENDPOINTS.post_request_analytics_custom_analytics(workspaceId);
  const requestBody = isTracesDataSource
    ? {
        filterGroup: effectiveFilterGroup,
        customCharts: customChartSpecs,
      }
    : {
        ...buildRequestLogQueryPayload(searchParams),
        customCharts: customChartSpecs,
      };

  const query = useQuery<RequestAnalyticsCustomChartsResponse>(
    [
      analyticsQueryKey,
      "analytics-custom-charts",
      "batched-dashboard",
      userToken,
      workspaceId,
      options?.dataSource,
      searchParams,
    ],
    () =>
      fetch(endpoint, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(requestBody),
      }).then(handleResponse),
    {
      enabled: isQueryEnabled && !!userToken && !!workspaceId && customChartSpecs.length > 0,
      keepPreviousData: true,
      retry: ANALYTICS_CHART_QUERY_RETRY_COUNT,
    },
  );

  const chartInterval = query.isPreviousData ? undefined : query.data?.chartInterval;
  const chartResponsesById = useMemo(
    () =>
      (query.data?.customCharts ?? []).reduce<
        Record<string, RequestAnalyticsCustomChartsResponse["customCharts"][number]>
      >((accumulator, chart) => {
        accumulator[chart.id] = chart;
        return accumulator;
      }, {}),
    [query.data?.customCharts],
  );

  return {
    chartInterval,
    chartResponsesById,
    isAnalyticsLoading: query.isFetching || query.isLoading,
    structuredDateRange,
  };
};
